import { useEffect, useRef, useState } from "react";

import LoadingButton from "@mui/lab/LoadingButton";
import Button from "@mui/material/Button";
import DialogActions from "@mui/material/DialogActions";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type { Theme } from "@mui/material/styles";

import { useAppSelector } from "app/hooks";
import Dialog from "components/dialog";
import { getPopupInNestedBillingDialogZIndex } from "constants/z-index";

import { SPEECH_TO_TEXT_SILENCE_DETECTION_SETTINGS } from "../word-processor-value.utils";
import { selectDataParamsById } from "../word-processor.slice";

type WordProcessorSilenceWarningDialogProps = {
  id: string;
  onContinue: () => void;
  onClose: () => void;
  onPause: () => void;
  // The host editor's nested-dialog layer, when it sits inside a raised modal (the scribe modal).
  // Without one it still opens above the app's other raised hosts (staff task modal, BillingDialog).
  dialogZIndex?: number;
};
const dialogContent = {
  sx: {
    padding: "20px 40px !important",
    minWidth: "320px",
    maxWidth: "320px"
  }
};

const INITIAL_COUNTDOWN =
  SPEECH_TO_TEXT_SILENCE_DETECTION_SETTINGS.silence_grace_period_sec - 1;

export default function WordProcessorSilenceWarningDialog({
  id,
  onContinue,
  onClose,
  onPause,
  dialogZIndex
}: WordProcessorSilenceWarningDialogProps) {
  const dataParams =
    useAppSelector((state: any) => selectDataParamsById(state, id)) || {};

  const open = Boolean(id && dataParams.showSilenceDialog);

  const [elapsedSeconds, setElapsedSeconds] = useState(INITIAL_COUNTDOWN);

  // Keep callbacks in refs to avoid dependency issues
  const onCloseRef = useRef(onClose);
  const onPauseRef = useRef(onPause);
  useEffect(() => {
    onCloseRef.current = onClose;
    onPauseRef.current = onPause;
  }, [onClose, onPause]);

  const handlePauseAndClose = () => {
    onPause();
    onClose();
  };

  // Reset countdown and start timer when dialog opens
  useEffect(() => {
    if (!open) {
      return;
    }

    // Reset to initial value when dialog opens
    setElapsedSeconds(INITIAL_COUNTDOWN);

    const interval = setInterval(() => {
      setElapsedSeconds((prev) => {
        const next = prev - 1;
        if (next <= 0) {
          clearInterval(interval);
          // Defer callbacks to avoid calling during state update
          setTimeout(() => {
            onPauseRef.current();
            onCloseRef.current();
          }, 0);
          return 0;
        }
        return next;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [open]);

  return (
    <Dialog
      title="Silence detected"
      onClose={onClose}
      open={open}
      minWidth={480}
      maxWidth="lg"
      dialogContentProps={dialogContent}
      sx={{
        zIndex: (theme: Theme) =>
          dialogZIndex ?? getPopupInNestedBillingDialogZIndex(theme)
      }}
    >
      <Stack width="100%" direction="column" rowGap={2} sx={{ mb: 2 }}>
        <Typography variant="body3" fontWeight={700}>
          Pause recording?
        </Typography>
      </Stack>

      <DialogActions>
        <Stack direction="row" gap={3}>
          <Button variant="outlined" onClick={handlePauseAndClose} size="small">
            Yes ({elapsedSeconds})
          </Button>
          <LoadingButton
            variant="contained"
            color="primary"
            onClick={onContinue}
            size="small"
          >
            No
          </LoadingButton>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
